"use client";

/**
 * AuditCta — the body-CTA used across the marketing surface (pricing hero,
 * pricing closing CTA, demo gallery, etc.). It now delegates to
 * <RunFirstCta/>, so:
 *
 *   Anonymous (or while loading):  opens the paste-a-URL run-first popover
 *                                  (a real guest audit, no login wall).
 *   Authenticated:                 links straight to /audit/new.
 *
 * Why this changed: it used to render a plain <Link href="/audit/new">,
 * which the app shell redirects to /login for guests — walling cold
 * visitors before they saw any value. Keeping the AuditCta name + props
 * means every existing call site (pricing, demo) becomes run-first with no
 * other edits. Purchase CTAs are intentionally not routed through here.
 */

import { RunFirstCta } from "./run-first-cta";

interface AuditCtaProps {
  /** Button className — defaults to the standard primary button styling. */
  className?: string;
  /**
   * Optional override for the guest label. Use sparingly — only when the
   * surrounding section needs different framing. The authenticated label
   * always stays "Start a new audit" for consistency.
   */
  guestLabel?: string;
  surface?: string;
}

const DEFAULT_GUEST_LABEL = "Start a free audit";
const AUTHENTICATED_LABEL = "Start a new audit";

export function AuditCta({
  className = "lp-btn lp-btn-primary",
  guestLabel = DEFAULT_GUEST_LABEL,
  surface = "marketing",
}: AuditCtaProps) {
  return (
    <RunFirstCta
      className={className}
      surface={surface}
      guestLabel={guestLabel}
      authedLabel={AUTHENTICATED_LABEL}
      authedHref="/audit/new"
    />
  );
}
